import { z } from 'zod';
// ─── Enums ───────────────────────────────────────────────────────────────────
export const RunStatusSchema = z.enum([
    'PENDING',
    'RUNNING',
    'PAUSED',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
    /**
     * Clean early exit triggered by a data-quality or precondition failure —
     * nothing broke, the pipeline correctly detected unusable input and stopped.
     * Distinct from FAILED (unexpected error) and CANCELLED (user-initiated).
     * Carries a structured `abortReason` (e.g. DATA_UNAVAILABLE,
     * CREDENTIAL_EXPIRED, INPUT_INVALID) so operators know what response is needed.
     */
    'ABORTED',
]);
/**
 * Known structured abort reason codes.
 * Node types throw `AbortRunError` with one of these (or a free-form string
 * for custom cases). The executor writes this value to `Run.abortReason`.
 */
const abortReasons = {
    DATA_UNAVAILABLE: 'DATA_UNAVAILABLE',
    CREDENTIAL_EXPIRED: 'CREDENTIAL_EXPIRED',
    INPUT_INVALID: 'INPUT_INVALID',
    /**
     * Cancelled by the Signal Orchestrator because an open Signal
     * already covers this underlying issue (dedup Mode A). The run links to
     * it via `Run.signalId`. (DEV-16223 / DEV-16224.)
     */
    DUPLICATE_OF_OPEN_SIGNAL: 'DUPLICATE_OF_OPEN_SIGNAL',
    /**
     * Cancelled by the Signal Orchestrator because another in-flight run
     * shares this run's `signalKey` (dedup Mode B). The run links to the
     * surviving primary via `Run.cancelledByRunId`.
     */
    DUPLICATE_OF_IN_FLIGHT_RUN: 'DUPLICATE_OF_IN_FLIGHT_RUN',
};
export { abortReasons as ABORT_REASONS };
/**
 * Throw from a node type's `execute()` to cleanly abort the run with a
 * structured reason. The executor catches this and writes ABORTED + reason
 * instead of FAILED, so operators can distinguish "pipeline crashed" from
 * "Snowflake had no data" from "PAT expired".
 */
export class AbortRunError extends Error {
    reason;
    constructor(reason, message) {
        super(message ?? reason);
        this.name = 'AbortRunError';
        this.reason = reason;
    }
}
export const RunTriggerSchema = z.enum(['MANUAL', 'API', 'WEBHOOK', 'SCHEDULED']);
export const NodeExecutionStatusSchema = z.enum([
    'PENDING',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'SKIPPED',
    'PAUSED',
]);
// ─── Run ─────────────────────────────────────────────────────────────────────
export const RunSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    workflowId: z.string(),
    workflowVersionId: z.string(),
    status: RunStatusSchema,
    trigger: RunTriggerSchema,
    input: z.record(z.string(), z.unknown()),
    output: z.record(z.string(), z.unknown()).nullable(),
    error: z.record(z.string(), z.unknown()).nullable(),
    /** One-line summary of what the run concluded (DEV-16832); null until terminal. */
    summary: z.string().nullable().optional(),
    /** Re-run controls (DEV-16843): { sourceRunId?, overlayMemoryIds?, dryRun? }. */
    overrides: z.record(z.string(), z.unknown()).nullable().optional(),
    abortReason: z.string().nullable(),
    idempotencyKey: z.string().nullable(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    totalCostUsd: z.number().nullable(),
    totalTokensIn: z.number().int().nonnegative(),
    totalTokensOut: z.number().int().nonnegative(),
    /**
     * Triage board column the run was tagged with based on the
     * `WorkflowEdge.boardColumnId` of the edge it traversed. Drives the
     * triage board's "outcome" view; null = no tag (run displays only in
     * the status view). (DEV-16183.)
     */
    boardColumnId: z.string().nullable().optional(),
    /** WorkflowSchedule that fired this run (DEV-17470); null for every other trigger. */
    scheduleId: z.string().nullable().optional(),
    /**
     * Who/what created the run: a principal subject for API triggers,
     * `schedule:<id>` for SCHEDULED, `system:<name>` for in-process creators.
     * Null for legacy rows and creators that pass no actor.
     */
    createdBySubject: z.string().nullable().optional(),
    /**
     * Dedup fields stamped by the Signal Orchestrator (DEV-16226 +
     * L2/L3). Null for runs that weren't deduplicated. The detail surfaces
     * use these to render a "deduplicated by L2 (sim 87%)" badge with the
     * reasoning text + a link back to the matched signal.
     */
    signalKey: z.string().nullable().optional(),
    signalId: z.string().nullable().optional(),
    cancelledByRunId: z.string().nullable().optional(),
    dedupReason: z.string().nullable().optional(),
    dedupLayer: z.enum(['L1', 'L2', 'L3']).nullable().optional(),
    dedupConfidence: z.number().min(0).max(1).nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateRunInputSchema = z.object({
    workflowId: z.string(),
    /** Optional — if omitted, uses the workflow's currentVersion. */
    workflowVersionId: z.string().optional(),
    input: z.record(z.string(), z.unknown()),
    idempotencyKey: z.string().min(1).max(200).optional(),
});
/**
 * Re-run an existing run (DEV-16843): same input, optionally pinned to a
 * specific workflow version, optionally with candidate-memory overlay pins
 * and/or dry-run (no side-effect ports). The idempotency key is deliberately
 * NOT copied — a re-run is a new run by definition.
 */
export const RerunInputSchema = z
    .object({
    workflowVersionId: z.string().optional(),
    /** Memory ids pinned into every llm-node's resolve (CANDIDATE allowed). */
    overlayMemoryIds: z.array(z.string()).max(50).optional(),
    /** Suppress side-effect ports (signals, S3 writes, MCP tools). */
    dryRun: z.boolean().optional(),
})
    .refine((v) => !(v.overlayMemoryIds && v.overlayMemoryIds.length > 0) || v.dryRun === true, {
    message: 'overlayMemoryIds requires dryRun: true — overlay pins bypass memory governance ' +
        '(CANDIDATE memories reach execution), so the run must not touch the outside world',
    path: ['dryRun'],
});
/**
 * Optional filters for `GET /v1/runs`. All fields are independent and
 * combine as AND. `status` accepts a single value or a comma-joined list
 * (the controller pre-splits before validation). Date strings are ISO
 * datetimes; `since`/`until` filter on `createdAt`.
 */
export const ListRunsQuerySchema = z
    .object({
    status: z.array(RunStatusSchema).min(1).max(7).optional(),
    workflowId: z.string().min(1).optional(),
    trigger: RunTriggerSchema.optional(),
    /**
     * Filter to runs an Evaluation enqueued. Each `POST /v1/evaluations/:id/run`
     * tags every Run it fans out with this id; re-runs accumulate. Surfaces
     * the full run history (including failed-pre-scoring runs that never
     * make it into `scoreSummary.perEntry`).
     */
    evaluationId: z.string().min(1).optional(),
    /** Filter to runs a WorkflowSchedule fired (DEV-17470); pairs with `trigger: SCHEDULED`. */
    scheduleId: z.string().min(1).optional(),
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
})
    .strict();
// ─── NodeExecution ───────────────────────────────────────────────────────────
export const NodeExecutionSchema = z.object({
    id: z.string(),
    runId: z.string(),
    nodeId: z.string(),
    /**
     * Human-readable name from the workflow graph (e.g. "classify",
     * "respond-urgent"). Joined from `WorkflowNode.nodeKey` by the
     * `/v1/runs/:id/node-executions` endpoint so the console can label
     * trace rows + message bubbles without a separate lookup. Null when
     * the parent WorkflowNode has been deleted out from under historic
     * executions. (DEV-16196.)
     */
    nodeKey: z.string().nullable().optional(),
    status: NodeExecutionStatusSchema,
    input: z.record(z.string(), z.unknown()).nullable(),
    output: z.record(z.string(), z.unknown()).nullable(),
    trace: z.record(z.string(), z.unknown()).nullable(),
    error: z.record(z.string(), z.unknown()).nullable(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nullable(),
    latencyMs: z.number().int().nullable(),
    createdAt: z.date(),
});
// ─── ToolInvocation ──────────────────────────────────────────────────────────
export const ToolInvocationSchema = z.object({
    id: z.string(),
    nodeExecutionId: z.string(),
    mcpConnectionId: z.string().nullable(),
    toolName: z.string(),
    input: z.record(z.string(), z.unknown()),
    output: z.record(z.string(), z.unknown()).nullable(),
    error: z.record(z.string(), z.unknown()).nullable(),
    validated: z.boolean(),
    schemaViolation: z.record(z.string(), z.unknown()).nullable(),
    latencyMs: z.number().int().nullable(),
    createdAt: z.date(),
});
// ─── HITL (Human-In-The-Loop) ────────────────────────────────────────────────
/**
 * One predefined response option attached to a paused run. The operator
 * picks one (or writes a free-form `override` for "other"). Each option's
 * `value` is what gets fed back to downstream nodes via the resolved-event
 * payload, so it can be any JSON shape the workflow author wants.
 */
export const HitlOptionSchema = z.object({
    /** Short, operator-readable label shown in the UI. */
    label: z.string().min(1).max(200),
    /** What gets passed downstream when this option is chosen. Free-form JSON. */
    value: z.unknown(),
    /** Optional longer help text rendered next to the label. */
    description: z.string().max(2000).optional(),
});
/**
 * POST /v1/runs/:id/pause body. All fields optional; bare `{}` still works
 * for "just pause this thing, I'll figure out the response context later."
 */
export const HitlPauseInputSchema = z
    .object({
    reason: z.string().max(2000).optional(),
    /** Operator-facing prompt: what is the human being asked to decide? */
    question: z.string().max(2000).optional(),
    /**
     * Predefined response options. Empty / omitted = free-form only;
     * the resume endpoint will only accept `override`. With options
     * present, the resume endpoint accepts `optionValue` (must match
     * one of these) or `override` for the "other" case.
     */
    options: z.array(HitlOptionSchema).max(50).optional(),
    /** Anything the operator needs to decide — prior outputs, snapshots, etc. */
    context: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
/**
 * POST /v1/runs/:id/resume body. Either `optionValue` (preferred when the
 * pause published `options`) or `override` (free-form). If both are sent,
 * `optionValue` wins. If neither and options were published, the API
 * rejects with 400.
 */
export const HitlResumeInputSchema = z
    .object({
    /** Match against one of the published options' `value` field. */
    optionValue: z.unknown().optional(),
    /** Free-form override. Used for "other" responses or when no options published. */
    override: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
/**
 * GET /v1/hitl/:runId response — everything the operator needs to decide.
 * Built from the run + the most-recent hitl.requested event payload.
 */
export const HitlContextSchema = z.object({
    runId: z.string(),
    workflowId: z.string(),
    workflowVersionId: z.string(),
    workflowName: z.string(),
    status: z.literal('PAUSED'),
    pausedAt: z.string().datetime().nullable(),
    reason: z.string(),
    question: z.string().nullable(),
    options: z.array(HitlOptionSchema),
    context: z.record(z.string(), z.unknown()),
    /** The run's input — operators want it to know what was being processed. */
    input: z.record(z.string(), z.unknown()),
});
/**
 * One row in the GET /v1/hitl/pending response — a paused run with just
 * enough context for an operator to pick which one to handle. The detail
 * page (GET /v1/hitl/:runId, returning {@link HitlContextSchema}) carries
 * the full options + context payload. (DEV-15912 / EU-45.)
 */
export const HitlQueueItemSchema = z.object({
    runId: z.string(),
    workflowId: z.string(),
    workflowName: z.string(),
    workflowVersionId: z.string(),
    input: z.record(z.string(), z.unknown()),
    /** ISO timestamp of when the run was paused (from the hitl.requested event). */
    pausedAt: z.string().datetime().nullable(),
    /** Human-readable reason provided at pause time. Empty string if none given. */
    reason: z.string(),
});
//# sourceMappingURL=run.js.map